import React, { useState } from "react";
import QRCode from "react-qr-code";
import { backendErrorMessage, readJsonResponse } from "../apiDiagnostics";
import { API_URL, FRONTEND_URL } from "../config";
import PageShell from "../components/PageShell";

const templates = {
  forum: {
    title: "Forum",
    items: [
      { name: "Montag", maxCount: 5 },
      { name: "Dienstag", maxCount: 5 },
      { name: "Mittwoch", maxCount: 5},
      { name: "Donnerstag", maxCount:5},
      { name: "Freitag", maxCount: 5}
    ]
  },
  klassendienst: {
    title: "Klassendienst",
    items: [
      { name: "Tafel putzen", maxCount: 1 },
      { name: "Fegen", maxCount: 1 },
      { name: "Müll rausbringen", maxCount: 1 },
      { name: "Fenster kontrollieren", maxCount: 1 },
      { name: "Material einsammeln", maxCount: 1 }
    ]
  },
    tdot:{
    title: "Sichten am Tag der offenen Tür",
    items: [
      { name: "Schicht 1 (Uhrzeit:)", maxCount: 1 },
      { name: "Schicht 2 (Uhrzeit:)", maxCount: 1 },
      { name: "Schicht 3 (Uhrzeit:)", maxCount: 1 },
      { name: "Schicht 4 (Uhrzeit:)", maxCount: 1 },
      { name: "Schicht 5 (Uhrzeit:)", maxCount: 1 }
    ]
  }
};

function TeacherPage() {
  const [title, setTitle] = useState(templates.forum.title);
  const [items, setItems] = useState(templates.forum.items.map(item => ({ ...item })));
  const [allowMultiple, setAllowMultiple] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("forum");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  function addItem() {
    setItems([...items, { name: "", maxCount: 1 }]);
  }

  function removeItem(index) {
    setItems(items.filter((_, i) => i !== index));
  }

  function updateItem(index, field, value) {
    setItems(items.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  }

  function copyToClipboard(inputfield) {
    // 1. Get the HTML input element
    const copyText = document.getElementById(inputfield);

    // 2. Write the element's value to the system clipboard
    navigator.clipboard.writeText(copyText.value)
        .then(() => {
          alert("Erfolgreich Kopiert!");
        })
        .catch(err => {
          console.error("Failed to copy text: ", err);
        });
  }

  function applyTemplate(templateKey) {
    setSelectedTemplate(templateKey);

    if (!templateKey) {
      setTitle("");
      setItems([{ name: "", maxCount: 1 }]);
      return;
    }

    const template = templates[templateKey];
    setTitle(template.title);
    setItems(template.items.map(item => ({ ...item })));
  }

  async function createList() {
    if (isCreating) return;

    setError("");
    setResult(null);

    const cleanItems = items
      .filter(item => item.name.trim() !== "")
      .map(item => ({
        name: item.name.trim(),
        maxCount: Math.max(1, Number(item.maxCount || 1))
      }));

    if (!title.trim()) return setError("Bitte geben Sie einen Namen für die Ordnungsliste ein.");
    if (cleanItems.length === 0) return setError("Bitte fügen Sie mindestens einen Eintrag hinzu.");

    const url = `${API_URL}/api/lists`;
    setIsCreating(true);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          items: cleanItems,
          listType: "ordnung",
          singleChoice: !allowMultiple
        })
      });

      const data = await readJsonResponse(response, "Ordnungsliste erstellen", url);
      if (!response.ok) return setError(data.error || "Ordnungsliste konnte nicht erstellt werden.");
      setResult(data);
    } catch (err) {
      setError(backendErrorMessage("Ordnungsliste erstellen", url, err));
    } finally {
      setIsCreating(false);
    }
  }

  const studentUrl = result ? FRONTEND_URL + result.studentLink : "";
  const teacherUrl = result ? FRONTEND_URL + result.teacherLink : "";
  const displayTitle = title.trim() || "Neue KlasseOrdnung";

  return (
    <PageShell maxWidth="max-w-3xl">
      <header className="mb-8">
        <p className="mb-3 text-xs font-medium uppercase tracking-[0.22em] text-neutral-500">KlasseOrdnung</p>
        <h1 className="text-4xl font-semibold tracking-[-0.04em]">{displayTitle}</h1>
        <p className="mt-3 text-base text-neutral-600">
          Erstellen Sie eine Ordnungsliste für Dienste, Tage oder Aufgaben und teilen Sie sie mit der Klasse.
        </p>
      </header>

      <section className="mb-5 rounded-2xl border border-[#dce8c8] bg-white p-5 shadow-sm">
        <label className="mb-2 block text-sm font-medium text-neutral-700">Vorlage</label>
        <select
          className="mb-4 w-full rounded-xl border-[#c9d9b4] px-4 py-3 text-base focus:border-[#6d8f36] focus:ring-[#6d8f36]"
          value={selectedTemplate}
          onChange={e => applyTemplate(e.target.value)}
        >
          <option value="">Ohne Vorlage starten</option>
          <option value="forum">Ordnungsdienst im Forum</option>
          <option value="klassendienst">Klassendienst</option>
          <option value="tdot">Schichten am Tag der offenen Tür</option>
        </select>

        <label className="mb-2 block text-sm font-medium text-neutral-700">Name der Ordnungsliste</label>
        <input
          className="w-full rounded-xl border-[#c9d9b4] px-4 py-3 text-base focus:border-[#6d8f36] focus:ring-[#6d8f36]"
          placeholder="z.B. KlasseOrdnung 8b"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />

        <label className="mt-4 flex items-start gap-3 rounded-xl border border-[#dce8c8] bg-[#f8faf2] px-4 py-3 text-sm text-neutral-700">
          <input
            className="mt-1 rounded border-[#c9d9b4] text-[#39501f] focus:ring-[#6d8f36]"
            type="checkbox"
            checked={allowMultiple}
            onChange={e => setAllowMultiple(e.target.checked)}
          />
          <span>
            <span className="block font-semibold text-neutral-800">Schüler dürfen mehrere Einträge wählen</span>
            <span className="mt-1 block text-neutral-500">
              Ausgeschaltet: Jeder Name kann nur einen Eintrag belegen. Eingeschaltet: Ein Schüler kann sich bei mehreren Aufgaben eintragen.
            </span>
          </span>
        </label>
      </section>

      <section className="mb-5 rounded-2xl border border-[#dce8c8] bg-white p-5 shadow-sm">
        <div className="mb-4">
          <h2 className="text-xl font-semibold tracking-[-0.02em]">Ordnungseinträge</h2>
          <p className="mt-1 text-sm text-neutral-500">Lege Aufgaben, Tage oder Zeiten fest und wie viele Plätze es jeweils gibt.</p>
        </div>

        <div className="space-y-3">
          {items.map((item, index) => (
            <div key={index} className="grid gap-3 rounded-xl border border-[#dce8c8] bg-[#f8faf2] p-3 sm:grid-cols-[1fr_100px_auto]">
              <input
                className="rounded-xl border-[#c9d9b4] px-4 py-2.5 text-sm focus:border-[#6d8f36] focus:ring-[#6d8f36]"
                placeholder="z.B. Montag oder Tafel putzen"
                value={item.name}
                onChange={e => updateItem(index, "name", e.target.value)}
              />
              <input
                aria-label="Freie Plätze"
                className="rounded-xl border-[#c9d9b4] px-3 py-2.5 text-center text-sm focus:border-[#6d8f36] focus:ring-[#6d8f36]"
                type="number"
                min="1"
                title="Freie Plätze"
                value={item.maxCount}
                onChange={e => updateItem(index, "maxCount", e.target.value)}
              />
              <button type="button" onClick={() => removeItem(index)} className="rounded-xl bg-white px-3 py-2.5 text-sm font-medium text-neutral-500 hover:text-red-700">
                Entfernen
              </button>
            </div>
          ))}
        </div>

        <button type="button" onClick={addItem} className="mt-3 w-full rounded-xl border border-[#c9d9b4] bg-[#eef5df] px-3 py-2.5 text-sm font-semibold text-[#39501f] hover:bg-[#e0edc8]">
          + Eintrag
        </button>

        {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error}</div>}

        <button
          type="button"
          onClick={createList}
          disabled={isCreating}
          className="mt-5 w-full rounded-xl bg-[#172017] px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-[#263826] disabled:cursor-not-allowed disabled:opacity-70"
        >
          {isCreating ? "Ordnungsliste wird erstellt..." : "KlasseOrdnung erstellen"}
        </button>
      </section>

      {result && (
        <section className="rounded-2xl border border-[#dce8c8] bg-white p-5 shadow-sm">
          <h2 className="text-xl font-semibold tracking-[-0.02em]">Ordnungsliste erstellt</h2>
          <p className="mt-2 text-sm text-neutral-500">Teilen Sie den Schülerlink oder zeigen Sie den QR-Code.</p>

          <div className="mt-5">
            <label className="mb-2 block text-sm font-medium text-neutral-700">Schülerlink</label>
            <input
                className="w-full rounded-xl border-[#c9d9b4] px-4 py-3 text-sm focus:border-[#6d8f36] focus:ring-[#6d8f36]"
                readOnly
                value={studentUrl}
                id="studentlink"
            />
            <div className="mt-2 flex gap-2">
              <button
                  type="button"
                  onClick={() => copyToClipboard("studentlink")}
                  className="px-4 py-3 rounded-xl bg-[#6d8f36] text-white text-sm"
              >
                Kopieren
              </button>
              <button
                  type="button"
                  onClick={() => window.open(studentUrl, "_blank", "noopener,noreferrer")}
                  className="px-4 py-3 rounded-xl border border-[#c9d9b4] bg-white text-[#39501f] text-sm"
              >
                Öffnen
              </button>
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-[#dce8c8] bg-[#f8faf2] p-5">
            <p className="mb-4 text-center text-sm font-medium text-neutral-700">QR-Code für Schüler</p>
            <div className="mx-auto flex w-fit rounded-xl bg-white p-4">
              <QRCode value={studentUrl} size={190} />
            </div>
            <p className="mt-4 text-center text-sm text-neutral-500">Mit der Kamera scannen und direkt eintragen.</p>
          </div>

          <div className="mt-5">
            <label className="mb-2 block text-sm font-medium text-neutral-700">Lehrerlink</label>
            <div className="mb-3 rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3 text-base font-bold text-red-800">
              Wichtig: Speichern Sie diesen Lehrerlink sofort ab. Ohne diesen Link können Sie die Ordnungsliste später nicht mehr verwalten, schließen, löschen oder als PDF speichern.
            </div>
            <input
                className="w-full rounded-xl border-[#c9d9b4] px-4 py-3 text-sm focus:border-[#6d8f36] focus:ring-[#6d8f36]"
                readOnly
                value={teacherUrl}
                id="teacherlink"
            />
            <div className="mt-2 flex gap-2">
              <button
                  type="button"
                  onClick={() => copyToClipboard("teacherlink")}
                  className="px-4 py-3 rounded-xl bg-[#6d8f36] text-white text-sm"
              >
                Kopieren
              </button>
              <button
                  type="button"
                  onClick={() => window.open(teacherUrl, "_blank", "noopener,noreferrer")}
                  className="px-4 py-3 rounded-xl border border-[#c9d9b4] bg-white text-[#39501f] text-sm"
              >
                Öffnen
              </button>
            </div>
          </div>
        </section>
      )}
    </PageShell>
  );
}

export default TeacherPage;
